import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Search, Trash2 } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import CustomerDetailDialog from "@/components/CustomerDetailDialog";
import { useConfirm } from "@/components/ConfirmDialog";

export default function Customers() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [form, setForm] = useState({ name: "", phone: "", email: "", address: "" });
  const [detailId, setDetailId] = useState(null);
  const canEdit = user?.role === "owner" || user?.role === "admin";

  const load = async () => {
    const [cs, vs] = await Promise.all([api.get("/customers"), api.get("/vehicles")]);
    setItems(cs.data);
    setVehicles(vs.data);
  };
  useEffect(() => { load(); }, []);

  const vehiclesByCustomer = useMemo(() => {
    const map = {};
    for (const v of vehicles) {
      (map[v.customer_id] = map[v.customer_id] || []).push(v);
    }
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicles]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return items;
    return items.filter((c) => {
      const vs = vehiclesByCustomer[c.id] || [];
      const haystack = [
        c.name, c.phone, c.email, c.address,
        ...vs.flatMap((v) => [v.brand, v.model, v.license_plate, v.vin]),
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(s);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items, vehiclesByCustomer, q]);

  const submit = async () => {
    try {
      await api.post("/customers", form);
      toast.success("OK");
      setOpen(false); setForm({ name: "", phone: "", email: "", address: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Error"); }
  };

  const del = async (id) => {
    if (!(await confirm())) return;
    await api.delete(`/customers/${id}`);
    load();
  };

  return (
    <div className="space-y-6" data-testid="customers-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-zinc-500">{t("customers.title")}</div>
          <h1 className="font-display text-3xl md:text-4xl font-semibold mt-1">{t("customers.title")}</h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-2.5 text-zinc-500" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("common.search")}
              className="pl-9 bg-[#171B24] border-[#2E3241] w-64" data-testid="customers-search" />
          </div>
          {canEdit && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-blue-500 hover:bg-blue-600" data-testid="new-customer-btn">
                <Plus className="h-4 w-4 mr-1" /> {t("customers.new_customer")}
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-[#21252E] border-[#2E3241] text-white">
              <DialogHeader><DialogTitle>{t("customers.new_customer")}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><Label className="text-xs">{t("common.name")}</Label>
                  <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="bg-[#171B24] border-[#2E3241]" data-testid="customer-name" /></div>
                <div><Label className="text-xs">{t("common.phone")}</Label>
                  <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-[#171B24] border-[#2E3241]" /></div>
                <div><Label className="text-xs">{t("common.email")}</Label>
                  <Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="bg-[#171B24] border-[#2E3241]" /></div>
                <div><Label className="text-xs">{t("common.address")}</Label>
                  <Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="bg-[#171B24] border-[#2E3241]" /></div>
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setOpen(false)}>{t("common.cancel")}</Button>
                <Button onClick={submit} className="bg-blue-500 hover:bg-blue-600" data-testid="create-customer-submit">{t("common.create")}</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
          )}
        </div>
      </div>

      <div className="surface rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#2A2F3A] text-zinc-400">
            <tr>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-widest">{t("common.name")}</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-widest">{t("common.phone")}</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-widest">{t("common.email")}</th>
              <th className="text-left px-4 py-3 text-xs uppercase tracking-widest">{t("common.address")}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? <tr><td colSpan={5} className="text-center text-zinc-500 py-8">{t("common.empty")}</td></tr>
              : filtered.map((c) => (
              <tr key={c.id}
                onClick={() => setDetailId(c.id)}
                className="border-t border-[#2E3241] hover:bg-[#2A2F3A] cursor-pointer"
                data-testid={`customer-row-${c.id}`}>
                <td className="px-4 py-3 font-medium">{c.name}</td>
                <td className="px-4 py-3 text-zinc-300">{c.phone}</td>
                <td className="px-4 py-3 text-zinc-300">{c.email}</td>
                <td className="px-4 py-3 text-zinc-400">{c.address}</td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>{canEdit && (
                  <Button size="icon" variant="ghost" onClick={() => del(c.id)} className="text-zinc-400 hover:text-red-400" data-testid={`customer-del-${c.id}`}><Trash2 className="h-4 w-4" /></Button>
                )}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <CustomerDetailDialog
        open={!!detailId}
        onOpenChange={(o) => !o && setDetailId(null)}
        customerId={detailId}
        canEdit={canEdit}
        onSaved={load}
      />
    </div>
  );
}
